import { memo, useCallback, useMemo, useState } from 'react';
import { Focusable, Key } from 'react-aria-components';

import {
  Button,
  Flex,
  Label,
  PillSelectValue,
  Radio,
  RadioGroup,
  SelectPills,
  SelectPillsOption,
  Separator,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { findField, FormatterValidatorHeader, ValidationFormSettings } from '@/components/ui';
import { useDebounceCallback, useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';
import { getCountryByLocale, Id, isArray } from '@/utils';

const SUPPORTED_LOCALES = [
  'am-Am',
  'ar-AE',
  'ar-BH',
  'ar-DZ',
  'ar-EG',
  'ar-EH',
  'ar-IQ',
  'ar-JO',
  'ar-KW',
  'ar-PS',
  'ar-SA',
  'ar-SD',
  'ar-SY',
  'ar-TN',
  'ar-YE',
  'az-AZ',
  'az-LB',
  'az-LY',
  'be-BY',
  'bg-BG',
  'bn-BD',
  'bs-BA',
  'ca-AD',
  'cs-CZ',
  'da-DK',
  'de-AT',
  'de-CH',
  'de-DE',
  'de-LU',
  'dv-MV',
  'dz-BT',
  'el-CY',
  'el-GR',
  'en-AG',
  'en-AI',
  'en-AU',
  'en-BM',
  'en-BS',
  'en-BW',
  'en-CA',
  'en-GB',
  'en-GG',
  'en-GH',
  'en-GY',
  'en-HK',
  'en-IE',
  'en-IN',
  'en-JM',
  'en-KE',
  'en-KI',
  'en-KN',
  'en-LS',
  'en-MO',
  'en-MT',
  'en-MU',
  'en-MW',
  'en-NG',
  'en-NZ',
  'en-PG',
  'en-PH',
  'en-PK',
  'en-RW',
  'en-SG',
  'en-SL',
  'en-SS',
  'en-TZ',
  'en-UG',
  'en-US',
  'en-ZA',
  'en-ZM',
  'en-ZW',
  'es-AR',
  'es-BO',
  'es-CL',
  'es-CO',
  'es-CR',
  'es-CU',
  'es-DO',
  'es-EC',
  'es-ES',
  'es-GT',
  'es-HN',
  'es-MX',
  'es-NI',
  'es-PA',
  'es-PE',
  'es-PY',
  'es-SV',
  'es-UY',
  'es-VE',
  'et-EE',
  'fa-AF',
  'fa-IR',
  'fi-FI',
  'fj-FJ',
  'fo-FO',
  'fr-BE',
  'fr-BF',
  'fr-BJ',
  'fr-CD',
  'fr-CF',
  'fr-FR',
  'fr-GF',
  'fr-GP',
  'fr-MQ',
  'fr-PF',
  'fr-RE',
  'fr-WF',
  'ga-IE',
  'he-IL',
  'hu-HU',
  'id-ID',
  'ir-IR',
  'it-IT',
  'it-SM',
  'ja-JP',
  'ka-GE',
  'kk-KZ',
  'kl-GL',
  'ko-KR',
  'ky-KG',
  'lt-LT',
  'mg-MG',
  'mn-MN',
  'mk-MK',
  'ms-MY',
  'my-MM',
  'mz-MZ',
  'nb-NO',
  'ne-NP',
  'nl-AW',
  'nl-BE',
  'nl-NL',
  'nn-NO',
  'pl-PL',
  'pt-AO',
  'pt-BR',
  'pt-PT',
  'ro-Md',
  'ro-RO',
  'ru-RU',
  'si-LK',
  'sk-SK',
  'sl-SI',
  'so-SO',
  'sq-AL',
  'sr-RS',
  'sv-SE',
  'tg-TJ',
  'th-TH',
  'tk-TM',
  'tr-TR',
  'uk-UA',
  'uz-UZ',
  'vi-VN',
  'zh-CN',
  'zh-HK',
  'zh-MO',
  'zh-TW',
];

export type PhoneNumberValidatorProps = {
  fieldId: string;
  validatorId: string;
};

const IPhoneNumberValidator = ({ fieldId, validatorId }: PhoneNumberValidatorProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const validator = field?.validators?.find((v) => v.id === validatorId);

  const [isSpecific, setIsSpecific] = useState(() => {
    if (isArray(validator?.config.locales) && validator.config.locales.length > 0) {
      return true;
    }
    return false;
  });

  const onSetLocales = useDebounceCallback(
    useCallback((locales: string[] | undefined) => mutate((f) => (f.config.locales = locales)), [mutate]),
    500
  );

  const buttonComponent = (
    <Button>
      <PillSelectValue>
        {(item?: Id | null) => {
          return item ? (
            <TooltipTrigger>
              <Tooltip variant="primary">
                <Text>{getCountryByLocale(item.id)?.name}</Text>
              </Tooltip>

              <Focusable>
                <Text>{getCountryByLocale(item.id)?.emoji}</Text>
              </Focusable>
            </TooltipTrigger>
          ) : null;
        }}
      </PillSelectValue>
      <ChevronIcon rotation="down" />
    </Button>
  );

  const onChangeSpecific = useCallback(
    (key: Key) => {
      setIsSpecific(key.toString() === 'specific');
      onSetLocales(undefined);
    },
    [onSetLocales]
  );

  const locales = useMemo(() => {
    return SUPPORTED_LOCALES.toSorted((a, b) => {
      const nameA = getCountryByLocale(a)?.name ?? '';
      const nameB = getCountryByLocale(b)?.name ?? '';
      return nameA.localeCompare(nameB);
    }).map((code) => ({ id: code }));
  }, []);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader validator={validator} />
      <Separator orientation="horizontal" />

      <Flex col className="gap-sm">
        <Label>Locales</Label>

        <RadioGroup onChange={onChangeSpecific} defaultValue={isSpecific ? 'specific' : 'all'}>
          <Radio value="all">
            <Text slot="description">All supported locales</Text>
          </Radio>
          <Radio value="specific">
            <Text slot="description">Select specific locales</Text>
          </Radio>
        </RadioGroup>

        {isSpecific ? (
          <SelectPills<Id>
            value={validator?.config.locales as string[] | undefined}
            onChange={(locales) => onSetLocales(locales.map((c) => c.toString()))}
            optionRenderFn={(option) => (
              <SelectPillsOption value={option}>
                {option ? (
                  <div className="gap-sm flex items-center">
                    <Text>{getCountryByLocale(option.id)?.emoji}</Text>
                    <Text>{getCountryByLocale(option.id)?.name}</Text>
                  </div>
                ) : null}
              </SelectPillsOption>
            )}
            items={locales}
            buttonComponent={buttonComponent}
          />
        ) : null}
      </Flex>

      <ValidationFormSettings fieldId={fieldId} validatorId={validatorId} />
    </div>
  );
};

export const PhoneNumberValidator = memo(IPhoneNumberValidator) as typeof IPhoneNumberValidator;
